import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./i18n"; // Initialize i18n
import App from "./App.tsx";
import "./index.css";

// 📱 Viewport Height Fix for Android/iOS
// Fixes vh issues with browser address bars
function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
}

setViewportHeight();
window.addEventListener('resize', setViewportHeight);
window.addEventListener('orientationchange', setViewportHeight);

createRoot(document.getElementById("root")!).render(
    <HelmetProvider>
        <App />
    </HelmetProvider>
);

// 🚨 Defer Sentry init until the browser is idle.
// Loading @sentry/react synchronously before React mount blocks the main thread
// (~50–150 ms on mid-range Android) and was contributing to the LCP regression
// observed on /signup and /onboarding (cf. Cloudflare Web Analytics, P75 = 4.5 s).
// This still captures errors that happen after first paint, which is when the
// vast majority of user-facing exceptions occur.
function bootSentry() {
    // Skip on localhost — never sent anyway, and saves the module download in dev.
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
        return;
    }
    import("@sentry/react")
        .then((Sentry) => {
            Sentry.init({
                dsn: import.meta.env.VITE_SENTRY_DSN,
                environment: import.meta.env.MODE,
                tracesSampleRate: 0.1,
                ignoreErrors: [
                    "ResizeObserver loop limit exceeded",
                    "Non-Error promise rejection captured",
                    "Loading chunk",
                ],
            });
        })
        .catch(() => {
            // Sentry is non-essential; swallow load failures silently.
        });
}

if (typeof window !== "undefined") {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback;
    if (typeof ric === "function") {
        ric(bootSentry, { timeout: 4000 });
    } else {
        setTimeout(bootSentry, 2000);
    }
}
